import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReAuth } from '@/api/baseApi';
import { dictionariesApi } from '@/api/dictionaries/api';
import { dictionariesTags } from '@/api/dictionaries/tags';
import { Dictionary } from '@/api/dictionaries/types';
import { vaccinationProtocolsTags } from '@/api/vaccination/protocols/tags';
import {
  EventProtocolData,
  EventProtocolDosagePayload,
  EventProtocolFilter,
  EventProtocolPayload,
  EventProtocolType,
} from '@/pages/Vaccination/components/protocols/types';
import { DICTIONARY_API, EVENT_PROTOCOL_BY_TYPE } from '@/shared/dict/api';

export const vaccinationProtocolsApi = createApi({
  reducerPath: 'vaccinationProtocolsApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(vaccinationProtocolsTags),
  endpoints: (builder) => ({
    getProtocols: builder.query<EventProtocolData, EventProtocolFilter>({
      query: (payload) => ({
        url: EVENT_PROTOCOL_BY_TYPE,
        method: 'GET',
        params: payload,
      }),
      transformResponse: (response: EventProtocolType[]) => {
        const eventProtocolRecord = {} as EventProtocolData;
        response.forEach((item) => (eventProtocolRecord[item.id] = item));
        return eventProtocolRecord;
      },
      providesTags: [vaccinationProtocolsTags.PROTOCOLS],
    }),
    createProtocol: builder.mutation<void, EventProtocolPayload>({
      query: (record) => ({
        url: DICTIONARY_API,
        method: 'POST',
        body: { ...record, type: 'eventProtocol' },
      }),
      invalidatesTags: [vaccinationProtocolsTags.PROTOCOLS],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(dictionariesApi.util.invalidateTags([dictionariesTags.DICTIONARIES]));
        } catch {
          // ошибка показывается общим обработчиком запросов
        }
      },
    }),
    updateProtocol: builder.mutation<void, EventProtocolPayload>({
      query: (record) => ({
        url: DICTIONARY_API,
        method: 'POST',
        body: { ...record, type: 'eventProtocol' },
      }),
      invalidatesTags: [vaccinationProtocolsTags.PROTOCOLS],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(dictionariesApi.util.invalidateTags([dictionariesTags.DICTIONARIES]));
        } catch {
          // ошибка показывается общим обработчиком запросов
        }
      },
    }),
    deleteProtocol: builder.mutation<void, { id?: string }>({
      query: (record) => ({
        url: `${DICTIONARY_API}/${Dictionary.EVENT_PROTOCOL}/${record.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [vaccinationProtocolsTags.PROTOCOLS],
    }),
    createDosage: builder.mutation<void, EventProtocolDosagePayload>({
      query: (record) => ({
        url: `${DICTIONARY_API}/eventProtocol/dosage`,
        method: 'POST',
        body: { ...record },
      }),
      invalidatesTags: [vaccinationProtocolsTags.PROTOCOLS],
    }),
    updateDosage: builder.mutation<void, EventProtocolDosagePayload>({
      query: (record) => ({
        url: `${DICTIONARY_API}/eventProtocol/dosage/${record?.id}`,
        method: 'PUT',
        body: { ...record },
      }),
      invalidatesTags: [vaccinationProtocolsTags.PROTOCOLS],
    }),
    deleteDosage: builder.mutation<void, { id?: string }>({
      query: (record) => ({
        url: `${DICTIONARY_API}/eventProtocol/dosage/${record?.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [vaccinationProtocolsTags.PROTOCOLS],
    }),
  }),
});

export const {
  useGetProtocolsQuery,
  useCreateProtocolMutation,
  useUpdateProtocolMutation,
  useDeleteProtocolMutation,
  useCreateDosageMutation,
  useUpdateDosageMutation,
  useDeleteDosageMutation,
} = vaccinationProtocolsApi;
